import MenuItem from "@mui/material/MenuItem";
import { styled, useTheme } from "@mui/material/styles";
import React, { FunctionComponent, ReactNode, useCallback, useState } from "react";
import { POPOVER_BOTTOM_START } from "designSystem/styles/popover-positions";
import { ComponentItem } from "designSystem/types/component-item-types";
import FlexRowCenter from "../flex/FlexRowCenter";
import { useLocale } from "designSystem/locale";
import {
	CSS_ANCHOR_RESET,
	itemListStyle,
	popperPaperStyle,
	secondaryActiveStyle,
	secondaryStyle,
	selectStyle
} from "designSystem/styles/style-functions";
import OverflowText from "../text/OverflowText";
import TextField, { TextFieldProps } from "../textfield/TextField";
import { TooltipProps } from "../tooltip/Tooltip";

const PREFIX = "Select";

const classes = {
	root: `${PREFIX}root`,
	menuItem: `${PREFIX}menuItem`,
	paper: `${PREFIX}paper`,
	select: `${PREFIX}select`,
	startIconMargin: `${PREFIX}startIconMargin`,
};

const StyledTextField = styled(TextField)(({
	theme,
}) => ({
	[`& .${classes.root}`]: {
		borderWidth: 1,
		padding: 0,
	},
	[`& .${classes.select}`]: {
		...selectStyle(theme),
	},
	[`& .${classes.startIconMargin}`]: {
		marginLeft: theme.spacing(1),
	},
}));

const menuStyles = (theme, constrainMenuWidth, width) => {
	const widthStyle = constrainMenuWidth ? { width } : null;

	return {
		...widthStyle,
		[`& .${classes.menuItem}`]: {
			...itemListStyle(theme),
		},
		[`& .${classes.paper}`]: {
			...popperPaperStyle(theme),
			paddingLeft: 0,
			paddingRight: 0,
		},
	};
};

export interface SelectTextFieldProps extends TextFieldProps {
	/** set to true to constrain the menu width to that of the component */
	constrainMenuWidth?: boolean;

	/** default item to select (maps to defaultValue) */
	defaultItem?: ComponentItem;

	/** the store of the menu */
	items: ComponentItem[];

	/** extension point when item is selected */
	onSelectItem?: (item: ComponentItem) => void;

	/** custom function for render dropdown item */
	renderItemFunc?: (item: ComponentItem) => ReactNode;

	/** custom function for render selected item */
	renderSelectedItemFunc?: (item: ComponentItem) => ReactNode;

	/** apply secondary styling vs normal textfield styling (different background color) */
	secondary?: boolean;

	/** selected item (maps to value) */
	selectedItem?: ComponentItem;

	/** Icon placed before the item */
	startIcon?: ReactNode;

	/** properties for tooltip if selected text overflows */
	tooltipProps?: Partial<TooltipProps>;
};

const Select: FunctionComponent<SelectTextFieldProps> = (props) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [width, setWidth] = useState(0);
	const theme = useTheme();
	const {
		constrainMenuWidth,
		defaultItem,
		defaultValue,
		items,
		onSelectItem,
		renderSelectedItemFunc,
		renderItemFunc,
		secondary,	// only used in styling, and not to be applied to TextField component
		selectedItem,
		startIcon,
		tooltipProps,
		value,
		...otherProps
	} = props;

	const loc = useLocale();

	const renderValue = (id: string): ReactNode => {
		const currentItem = items.find((item) => item.id == id);
		const text = loc.getComponentItemName(currentItem);

		return (
			renderSelectedItemFunc ?
				renderSelectedItemFunc(currentItem)
				: (
					<FlexRowCenter>
						{startIcon}
						<OverflowText
							className={startIcon ? classes.startIconMargin : ""}
							text={text}
							tooltipProps={tooltipProps}
						/>
					</FlexRowCenter>
				)
		);
	};

	const contentRef = useCallback((node: HTMLElement) => {
		if (node) {
			setWidth(node.offsetWidth);
		}
	}, [constrainMenuWidth]);

	const menuOpenStyles = menuOpen ? secondaryActiveStyle(theme, !props.secondary) : {};
	const inputStyles = {
		...secondaryStyle(theme, !props.secondary),
		...menuOpenStyles,
	};

	return (
		<StyledTextField
			InputProps={{
				className: classes.root,
				sx: inputStyles,
			}}
			SelectProps={{
				classes: {
					select: classes.select,
				},
				MenuProps: {
					PopoverClasses: {
						paper: classes.paper,
					},
					sx: menuStyles(theme, constrainMenuWidth, width),
					...POPOVER_BOTTOM_START,
				},
				onClose: () => setMenuOpen(false),
				onOpen: () => setMenuOpen(true),
				renderValue,
			}}
			defaultValue={defaultValue ?? defaultItem?.id}
			ref={contentRef}
			select
			value={value ?? selectedItem?.id}
			{...otherProps}
		>
			{
				items.map((item) => (
					<MenuItem
						className={`${CSS_ANCHOR_RESET} ${classes.menuItem}`}
						component={item.href ? "a" : "li"}
						href={item.href}
						key={item.id}
						onClick={() => onSelectItem?.(item)}
						value={item.id}
					>
						{
							renderItemFunc ?
								renderItemFunc(item)
								: (
									<OverflowText
										text={loc.getComponentItemName(item)}
										tooltipProps={tooltipProps}
									/>
								)}
					</MenuItem>
				))
			}
		</StyledTextField>
	);
};

export default Select;